/**
 * LE MENU RÉDUIT — maquette validée par Dove le 15/09. Trois groupes nommés par
 * le profil (« Ma journée · Qui · Parler »), puis « Plus… », un `details` replié
 * par défaut et mémorisé PAR PERSONNE dans le navigateur : Élodie qui l'ouvre
 * une fois le retrouve ouvert, Dove sur le même poste ne le voit pas (son menu
 * est complet).
 */
import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { aide } from "./aide/aides";
import { Icon } from "./icons";
import { LienNav } from "./LienNav";
import type { NavReduite as ArbreReduit } from "./registry";

export const CLE_PLUS = "geceram.menu.plus";

/** `localStorage` peut être absent ou refusé (navigation privée) : on lit sans planter. */
export function lirePlusOuvert(): boolean {
  try {
    return localStorage.getItem(CLE_PLUS) === "1";
  } catch {
    return false;
  }
}

export function ecrirePlusOuvert(ouvert: boolean): void {
  try {
    localStorage.setItem(CLE_PLUS, ouvert ? "1" : "0");
  } catch {
    // rien à faire : le menu reste utilisable, il n'est juste pas mémorisé
  }
}

export function NavReduite({ nav, onClick }: { nav: ArbreReduit; onClick: () => void }) {
  const { pathname } = useLocation();
  const memorise = lirePlusOuvert();
  /*
   * F7 (Dove 16/09) : Élodie qui arrive sur /pilotage (Tableau de bord, sous
   * « Plus… ») doit voir où elle est — pas un menu entièrement éteint. Ce
   * dépliage est un CONSTAT (« la page courante est ici »), pas un geste
   * d'Élodie : il ne doit donc jamais s'écrire dans la mémoire par personne.
   *
   * Piège : poser `open` à `true` dès le premier rendu déclenche, de façon
   * asynchrone, un événement `toggle` — même quand personne n'a rien cliqué.
   * `ignorerPremierToggle` avale UNIQUEMENT ce premier écho, pour ne pas le
   * confondre avec un vrai clic sur « Plus… ».
   */
  const autoOuvert = !memorise && nav.plus.some((m) => m.routes[0]?.path === pathname);
  const [ouvert] = useState(() => memorise || autoOuvert);
  const ignorerPremierToggle = useRef(autoOuvert);
  return (
    <>
      {nav.groupes.map((g) => (
        <div key={g.libelle}>
          <div className="grp">{g.libelle}</div>
          {g.modules.map((mod) => <LienNav key={mod.id} mod={mod} onClick={onClick} />)}
        </div>
      ))}
      {nav.plus.length > 0 ? (
        <details
          className="plus"
          open={ouvert}
          onToggle={(e) => {
            const nouveau = (e.currentTarget as HTMLDetailsElement).open;
            if (ignorerPremierToggle.current) {
              ignorerPremierToggle.current = false;
              if (nouveau) return;
            }
            ecrirePlusOuvert(nouveau);
          }}
        >
          <summary {...aide("nav.plus")}>
            <Icon id="i-grid" />
            <span className="lbl">Plus…</span>
            <Icon id="i-chevron" size="sm" />
          </summary>
          {nav.plus.map((mod) => <LienNav key={mod.id} mod={mod} onClick={onClick} />)}
        </details>
      ) : null}
    </>
  );
}
